import { useEffect, useMemo, useState } from 'react'
import { isAxiosError } from 'axios'
import { Formik } from 'formik'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { useUpdateUser } from '@dhl/mdp-api'

import { Box, Grid } from '@dhl-ui/components'
import { links } from '~/AppRoutes'
import { useNavigateWithSearchString, useNotificationMessage, useSearchQuery } from '~/common/hooks'
import { UUID } from '~/common/model'
import {
  CancelDialog,
  DangerZoneCard,
  EditUserOrganisationCard,
  ProfileCard,
  ProfileForm,
  RolesForm,
  UsersAccountManagerCard,
  ViewUserOrganisationCard,
} from '~/modules/users/components'
import { useUser } from '~/modules/users/hooks'
import { convertToBaseUser, createBaseUserValidationSchema, type BaseUser } from '~/modules/users/model'
import { BackButton } from '~/temp-ds-component/BackButton'

type Params = {
  userId: UUID
}

export const UserDetailPage = () => {
  const navigate = useNavigate()
  const navigateWithSearchString = useNavigateWithSearchString(true)
  const { state } = useLocation()
  const { userId } = useParams<Params>()
  const { t } = useTranslation()
  const { showError, showSuccess } = useNotificationMessage()
  const [cancelDialogOpen, setCancelDialogOpen] = useState<boolean>(false)
  const [isOrganisationChanged, setIsOrganisationChanged] = useState<boolean>(false)
  const labelRequestPid = useSearchQuery('labelPid')
  const isFromLabelRequest = !!labelRequestPid

  const [isEditingProfile, setIsEditingProfile] = useState<boolean>(false)
  const [isEditingOrganization, setIsEditingOrganization] = useState<boolean>(false)
  const [isEditingRoles, setIsEditingRoles] = useState<boolean>(false)
  const { user, refetch: refreshUser, error } = useUser(userId!)
  const isAccountManager = user?.userType === 'accountManager'

  const { mutateAsync: updateUser, error: updateError } = useUpdateUser()

  const onBack = (dirty: boolean) => {
    if (isEditingProfile && dirty && userId) {
      setCancelDialogOpen(true)
    } else if (state && state.from.includes('organisations')) {
      navigate(links.organisationDetailsLink(state.id))
    } else {
      isFromLabelRequest
        ? navigate({ pathname: links.labelLink(), search: `?labelPid=${labelRequestPid}` })
        : navigateWithSearchString(links.userListLink())
    }
  }

  const handleOrganisationEditCancel = async () => {
    setIsEditingOrganization(false)
    if (isOrganisationChanged) {
      await refreshUser()
    }
  }

  const handleOrganisationChanged = () => {
    refreshUser().then(() => {
      setIsOrganisationChanged(true)
    })
  }
  const handleUserRemoved = () => navigate(links.userListLink())
  const handleUserDisconnected = () => refreshUser()
  const handleRolesUpdated = () => {
    setIsEditingRoles(false)
    refreshUser() // this forces a refresh of the roles in the `RolesCard`
  }

  useEffect(() => {
    if (error && isAxiosError(error)) {
      showError(error.message)
    }
  }, [error])

  useEffect(() => {
    if (updateError && isAxiosError(updateError)) {
      showError(updateError.message)
    }
  }, [updateError])

  const onSubmit = async (user: BaseUser) => {
    if (userId) {
      await updateUser(
        { emailOrId: userId, data: user },
        {
          onSuccess: () => showSuccess(t('admin.adminUsersPage.userForm.updateUserMessage')),
          onError: () => showError(t('admin.unknownError')),
        }
      )
      await refreshUser()
      setIsEditingProfile(false)
    }
  }

  const validationSchema = createBaseUserValidationSchema(t)

  const baseUser: BaseUser = useMemo(() => convertToBaseUser(user), [user])

  return user ? (
    <>
      <Formik
        initialValues={baseUser}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
        validateOnChange={false}
        enableReinitialize
      >
        {({ dirty, resetForm }) => (
          <Grid container direction="column">
            <Grid>
              <Box marginBottom={2}>
                <BackButton onClick={() => onBack(dirty)}>
                  {t(
                    isFromLabelRequest
                      ? 'admin.usersPage.userDetails.titleTextLabel'
                      : 'admin.usersPage.userDetails.titleText'
                  )}
                </BackButton>
              </Box>
            </Grid>
            <Grid container direction="row" spacing={2}>
              <Grid size={{ xs: 6 }}>
                <Grid container direction="column" spacing={2}>
                  <Grid>
                    {isEditingProfile ? (
                      <ProfileForm
                        user={user}
                        onCancel={() => {
                          setIsEditingProfile(false)
                          resetForm()
                          refreshUser().then(() => {})
                        }}
                      />
                    ) : (
                      <ProfileCard user={user} onEditClick={() => setIsEditingProfile(true)} />
                    )}
                  </Grid>
                  {isAccountManager && (
                    <Grid>
                      <UsersAccountManagerCard user={user} />
                    </Grid>
                  )}
                  <Grid>
                    <DangerZoneCard
                      user={user}
                      onUserRemoved={handleUserRemoved}
                      onUserDisconnected={handleUserDisconnected}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Grid container direction="column" spacing={2}>
                  {!isAccountManager && (
                    <Grid>
                      {isEditingOrganization ? (
                        <EditUserOrganisationCard
                          user={user}
                          onOrganisationChanged={handleOrganisationChanged}
                          onCancel={handleOrganisationEditCancel}
                        />
                      ) : (
                        <ViewUserOrganisationCard user={user} onEdit={() => setIsEditingOrganization(true)} />
                      )}
                    </Grid>
                  )}
                  <Grid>
                    <RolesForm
                      user={user}
                      onSaveOrCancel={handleRolesUpdated}
                      onEditButtonClick={() => setIsEditingRoles(true)}
                      isEditingRoles={isEditingRoles}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Formik>
      <CancelDialog open={cancelDialogOpen} onCancel={() => setCancelDialogOpen(false)} />
    </>
  ) : null
}
